import { Select, type SelectOption } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useOnGamesSwitch } from "../../components/nocs/use-list-page";
import { Card } from "../../components/page/card";
import { PageHeader } from "../../components/page/page-header";
import { ReportCatalogue } from "../../components/reports/catalogue";
import { Button } from "../../components/ui/button";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { errorMessage } from "../../lib/api/messages";
import { gamesSportsQuery, useCurrentGames } from "../../lib/games";

/** `?sport=<id>` — the sport whose reports are shown; a bad value is dropped. */
const reportsSearch = z.object({ sport: z.string().uuid().optional().catch(undefined) });

/**
 * Results › Reports — the old OVR's report catalogue, for one sport of the
 * current games: competition schedule (whole or one day), a meet's start
 * list / results, medallists by event and the medal standing. Each is an A4
 * sheet printed by the browser (`components/print`'s print CSS), so "Save as
 * PDF" is the PDF; no versions or signatures are stored.
 */
export const Route = createFileRoute("/_app/reports")({
	validateSearch: reportsSearch,
	component: ReportsPage,
});

function ReportsPage() {
	const { t } = useTranslation("reports");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: "/reports" });
	const current = useCurrentGames();
	const gamesId = current.gamesId;
	const sports = useQuery({ ...gamesSportsQuery(gamesId ?? ""), enabled: gamesId !== null });
	const pickId = useId();

	// The sidebar switched games: the sport belongs to the old one.
	useOnGamesSwitch(gamesId, () => void navigate({ search: {}, replace: true }));

	const options = useMemo<SelectOption[]>(
		() => (sports.data ?? []).map((s) => ({ value: s.sport_id, label: s.name, description: s.code })),
		[sports.data],
	);
	const sport = sports.data?.find((s) => s.sport_id === search.sport);

	if (current.error) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("errors.gamesTitle")} body={errorMessage(current.error)} />
			</div>
		);
	}
	if (current.isLoading) return <Spinner className="size-[1.5rem] text-board" label={t("loading")} />;
	if (!current.games || gamesId === null) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.noGamesTitle")} body={t("empty.noGamesBody")} />
			</div>
		);
	}

	return (
		<div className="flex flex-col">
			<PageHeader title={t("title")} subtitle={t("subtitle", { games: current.games.name })} />
			<div className="flex flex-col gap-[1rem]">
				<Card className="flex flex-col gap-[0.75rem] p-[1rem]">
					{sports.isPending ? (
						<Spinner className="size-[1.25rem] text-board" label={t("loading")} />
					) : sports.isError ? (
						<div className="flex flex-wrap items-center gap-[0.75rem] text-[0.875rem] text-conflict" role="alert">
							{errorMessage(sports.error)}
							<Button size="sm" variant="outline" onClick={() => void sports.refetch()}>
								{t("retry")}
							</Button>
						</div>
					) : options.length === 0 ? (
						<EmptyState title={t("empty.noSportsTitle")} body={t("empty.noSportsBody")} />
					) : (
						<div className="flex max-w-[24rem] flex-col gap-[0.25rem]">
							<label htmlFor={pickId} className="font-bold text-[0.8125rem] text-ink">
								{t("sport")}
							</label>
							<Select
								id={pickId}
								options={options}
								value={sport?.sport_id ?? null}
								placeholder={t("pickSport")}
								onChange={(v) => void navigate({ search: { sport: v ?? undefined } })}
							/>
						</div>
					)}
					<p className="text-[0.8125rem] text-muted">{t("scope")}</p>
				</Card>

				{sport ? (
					<ReportCatalogue key={sport.sport_id} gamesId={gamesId} gamesName={current.games.name} sportId={sport.sport_id} sportName={sport.name} />
				) : (
					options.length > 0 && <EmptyState title={t("pickSport")} body={t("pickSportBody")} />
				)}
			</div>
		</div>
	);
}
